import React from "react";
import { cloneDeep } from "lodash";
import {
  ResultComparison,
  ScaleComponent,
  GelDragDrop,
} from "@learningblocks/storybook-components";
import "./style.scss";

const params = {
  "gelTheme": "DARK",
  "title": "Drag the results you predict into each lane",
  "heading": "Predict the gel results:",
  "text": "Based on your understanding of PCR, drag the bands in each lane from the well to where you would expect to see them relative to the DNA ladder in lane 1. If you do not expect to see a band in a lane, drag the band from the well to the area below the gel to discard it.\n\nThe gel lanes include the DNA ladder, the sample with unknown extracted DNA from a patient, two positive controls (Control 1 and 2), and two negative controls (Control 3 and 4).\n\nControl 1 = Positive control: normal, full length, <i>GENE X</i>\n\nControl 2 = Positive control: mutated, shorter, <i>GENE X</i>\n\nControl 3 = Negative control: no DNA template\n\nControl 4 = Negative control: prokaryotic DNA template that does not contain <i>GENE X</i>",
  "gelRange": {
    "bottom": 80,
    "top": 1000
  },
  "content": [
    "heading",
    "text",
    "dragdrop"
  ],
  "gelTable": [
    {
      "colId": 0,
      "colType": "LABEL_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": ""
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": null,
          "label": ""
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 1000,
              "selected": false
            },
            {
              "gelId": 1,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 700,
              "selected": false
            },
            {
              "gelId": 2,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 500,
              "selected": false
            },
            {
              "gelId": 3,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 300,
              "selected": false
            },
            {
              "gelId": 4,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 100,
              "selected": false
            }
          ]
        },
        {
          "cellId": 3,
          "cellType": "TRASH_LABEL"
        }
      ]
    },
    {
      "colId": 1,
      "colType": "LADDER_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "DNA ladder"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": []
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 0.7,
              "value": 1000,
              "selected": false
            },
            {
              "gelId": 2,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 0.6,
              "value": 700,
              "selected": false
            },
            {
              "gelId": 3,
              "gelType": "FIXED",
              "height": 80,
              "opacity": 0.9,
              "value": 500,
              "selected": false
            },
            {
              "gelId": 4,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 0.7,
              "value": 300,
              "selected": false
            },
            {
              "gelId": 5,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 0.6,
              "value": 100,
              "selected": false
            }
          ]
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 2,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C1"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 3,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C2"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 4,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C3"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 5,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C4"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 6,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "S"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            },
            {
              "gelId": 1,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    }
  ]
};

export class ResultsComparisonGel extends React.Component {

  idealData = {
    "DNA A": {
      "S": [
        1000,
        0
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    },
    "DNA B": {
      "S": [
        700,
        0
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    },
    "DNA C": {
      "S": [
        1000,
        700
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    }
  }

  getIdealLanes = (data) => {
    data.gelTable = data.gelTable.map((col) => {

      // Rearrange the gel_holder and gel_path props
      if(col.colItems[1].cellType === "GEL_HOLDER" && col.colItems[1].cellItems !== null && col.colItems[1].cellItems.length > 0){
        if(col.colItems[2].cellType === "GEL_PATH"){
          col.colItems[2].cellItems = col.colItems[1].cellItems;
          col.colItems[1].cellItems = [];
        }
      }

      // Insert the Ideal values
      col.colItems = col.colItems.map((item) => {
          if(item.cellType === "GEL_PATH" && item.cellItems !== null){
            item.cellItems = item.cellItems.filter((band, bandIndex)=>{
              //let key = Object.keys(this.props.interactionProps.solutions[14].solutions)[0];
              //if(this.idealData[key].hasOwnProperty(col.colItems[0].label)){
                //band.value = this.idealData[key][col.colItems[0].label][bandIndex];
              //}
              if(band.value !== 0){
                return band;
              }
            })
          }
          return item;
      });
      return col;
    });

    return data;
  }

  actualData = {
    "DNA A": {
      "S": [
        1000,
        0
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    },
    "DNA B": {
      "S": [
        700,
        0
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    },
    "DNA C": {
      "S": [
        1000,
        700
      ],
      "C1": [
        1000
      ],
      "C2": [
        700
      ]
    }
  }

  getActualLanes = (data) => {

    let solutionIDs = {
      "S": 1,
      "C1": 2,
      "C2": 3,
      "C3": 4,
      "C4": 5,
      "Extracted human DNA": 14
    }

    data.gelTable = data.gelTable.map((col) => {

      // Rearrange the gel_holder and gel_path props
      if(col.colItems[1].cellType === "GEL_HOLDER" && col.colItems[1].cellItems !== null && col.colItems[1].cellItems.length > 0){
        if(col.colItems[2].cellType === "GEL_PATH"){
          //col.colItems[2].cellItems = col.colItems[1].cellItems;
          col.colItems[1].cellItems = [];
        }
      }

      // If basic requirements for a result fufilled
      if(this.props.prepareGelBox &&
         this.props.runGel &&
         //this.props.visualizeGel &&
         this.props.loadDNALadder){

           // Insert the Actual values
           col.colItems = col.colItems.map((item) => {
               if(item.cellType === "GEL_PATH" && solutionIDs.hasOwnProperty(col.colItems[0].label)){

                 let solution = this.props.interactionProps.solutions[solutionIDs[col.colItems[0].label] - 1];
                 let id = 0;

                  // Prerequisite conditions for a result
                  if(
                    solution.solutions["Taq DNA Polymerase"] >= 0.5 &&
                    solution.solutions["Buffer"] >= 2.5 &&
                    solution.solutions["dNTPs"] >= 0.5 &&
                    (
                      (
                        solution.solutions["Forward Primer"] >= 0.5 &&
                        solution.solutions["Reverse Primer"] >= 0.5
                      )
                      ||
                      (
                        solution.solutions["Primer Mix of Primers 1 and 8"] >= 1 //||
                        // solution.solutions["Primer Mix of Primers 2 and 5"] >= 1 ||
                        // solution.solutions["Primer Mix of Primers 3 and 4"] >= 1
                      )
                    )
                  ){

                    // Lane conditions for banding data
                    if(solution.solutions["DNA A"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 1000,
                        "selected": false
                      })
                      id++;
                    }
                    // Lane conditions for banding data
                    if(solution.solutions["DNA B"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 700,
                        "selected": false
                      })
                      id++;
                    }
                    // Lane conditions for banding data
                    if(solution.solutions["DNA C"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 1000,
                        "selected": false
                      })
                      id++;
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 700,
                        "selected": false
                      })
                      id++;
                    }

                    if(solution.solutions["GENE X Normal Length Control DNA"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 1000,
                        "selected": false
                      })
                      id++;
                    }

                    if(solution.solutions["GENE X Shorter Length Control DNA"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": 1,
                        "value": 700,
                        "selected": false
                      })
                      id++;
                    }

                    // These have no result
                    //if(solution.solutions["dH20"] > 1){}
                    //if(solution.solutions["Prokaryotic Control DNA"] >= 1){}
                  }

               }
               return item;
           });
      }

      return col;
    });

    return data;
  }

  getIdealDescription = () => {

    let solutionIDs = {
      "S": 5,
      "C1": 1,
      "C2": 2,
      "C3": 3,
      "C4": 4,
      "Extracted human DNA": 14
    }
    let varSolution = "";
    let solObject = this.props.interactionProps.solutions[solutionIDs["Extracted human DNA"] - 1].solutions;

    if(solObject !== null){
      varSolution = Object.keys(solObject)[0];
    }

    let description = "";

    switch(varSolution) {
      case "DNA A":
        // code block
        description = "The sample lane should have one band that is approximately 1000 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
        break;
      case "DNA B":
        // code block
        description = "The sample lane should have one band that is approximately 700 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
        break;
      case "DNA C":
        // code block
        description = "The sample lane should have two bands. One band that is approximately 1000 bp and another band that is approximately 700 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
        break;
      default:
        // code block
    }

    return description;
  }

  addLane(data, index, solutionAlias, bandType) {
    data.gelTable.push(this.getLane(bandType, index, solutionAlias));
  }

  render() {
    return (
      <ResultComparison
        predictedState={{
          type: "component",
          component: (
            <>
              <p>These are your initial predictions.</p>
              <GelDragDrop
                {...this.props.gelDragDropPrediction}
                useAbbrevLabel={true}
              />
            </>
          ),
        }}
        actualState={{
          type: "component",
          component: (
            <>
              <p>These are your actual results.</p>
              <GelDragDrop {...this.getActualLanes(cloneDeep(params))} />
            </>
          ),
        }}
        idealState={{
          type: "component",
          component: (
            <>
              <p>These are your ideal results.</p>
              <GelDragDrop {...this.getIdealLanes(cloneDeep(params))} />
              {/*<p>
                {this.getIdealDescription()}
              </p>*/}
            </>
          ),
        }}
      />
    );
  }
}
